import type { ReactNode } from "react"

/** Triage state shared by widgets, rows, nav counts and pills (brain.aixlabs.co vocabulary). */
export enum EVerdict {
  ACT = "act",
  WATCH = "watch",
  UNKNOWN = "unknown",
  CLEAR = "clear",
}

export interface IWidgetPill {
  text: string
  verdict: EVerdict
}

/** Row spacing, chosen per widget per device from the widget menu or `d`. */
export enum EDensity {
  COMPACT = "compact",
  STANDARD = "standard",
  RELAXED = "relaxed",
}

/**
 * Letters a row action may bind. The shell owns everything else: digits, h j k l (and arrows) to move,
 * o open, d density, c collapse, r refresh, / filter, Enter, Esc. A widget can't claim a shell key: it won't compile.
 */
export type TRowActionKey =
  | "a"
  | "b"
  | "e"
  | "f"
  | "g"
  | "i"
  | "m"
  | "n"
  | "p"
  | "q"
  | "s"
  | "t"
  | "u"
  | "v"
  | "w"
  | "x"
  | "y"
  | "z"

interface IRowActionBase {
  key: TRowActionKey
  label: string
  disabled?: boolean
}

/** A row button, also bound to `key` while the row is selected. Rendered by the shell, never by the widget. */
export type IRowAction =
  | (IRowActionBase & { run: () => void })
  /** Multi-step actions (a reason, a confirm) live in `details`; the key opens the row and focuses its first field */
  | (IRowActionBase & { expand: true })

export interface IWidgetRow {
  key: string
  verdict: EVerdict
  /** Defaults to the verdict itself */
  pill?: IWidgetPill
  title: string
  sub?: string
  meta?: string
  /** Shown when the row is expanded: context only. Links and buttons go in href/actions. */
  details?: ReactNode
  /** The row's primary link: `o` opens it ("/…" in the app, anything else in a new tab) */
  href?: string
  /** Label for the href button; defaults to "Open" */
  hrefLabel?: string
  actions?: IRowAction[]
  /**
   * Which company/product the row belongs to, as whatever key the source has: a company slug, an EProject slug
   * (incident.project), a deploy projectName ("vidmax-vite") or a company name. The shell resolves it; rows without
   * one are hidden while a company filter is on.
   */
  company?: string
  /** Quiet rows collapse into one "N more" line at the end */
  quiet?: boolean
}

/** What every widget hands the shell. Rows sort by verdict unless `ordered`. */
export interface IWidgetResult {
  verdict: EVerdict
  count?: string
  lead?: ReactNode
  rows: IWidgetRow[]
  ordered?: boolean
  quietLabel?: string
  empty?: string
  foot?: string
  /** Drop the widget entirely (nothing worth showing) */
  hide?: boolean
}

export interface IWidgetState {
  result?: IWidgetResult
  error?: string
  isLoading: boolean
  isFetching: boolean
  refetch: () => void
  updatedAt?: number
}

export type TColumnCount = 1 | 2 | 3 | 4 | 5 | 6

export interface IWidgetDefinition {
  id: string
  title: string
  description: string
  /** Default 1-based column per column count; missing counts fall back to the nearest smaller one */
  columns: Partial<Record<TColumnCount, number>>
  link?: { href: string; label: string }
  /** Company slugs this whole widget is about (Mission/Bets → ["vidmax"]); hidden when the company filter excludes them */
  companies?: string[]
  /** Starting density before the user picks one; STANDARD when omitted */
  defaultDensity?: EDensity
  useWidget: () => IWidgetState
}

/** One dashboard layout. Mirrors apps/aixinternal-api/src/service/homeDashboardPrefs.ts */
export interface IHomeDashboardLayout {
  hidden: string[]
  snoozes: Record<string, string>
  placement: Record<string, Record<string, number>>
  order: string[]
}

/** The top-level layout is the Home view; `views` holds the other views' layouts by id (viewRegistry.ts) */
export interface IHomeDashboardPrefs extends IHomeDashboardLayout {
  views: Record<string, IHomeDashboardLayout>
}

/** What a mounted widget reports to the shell for nav counts. */
export interface IWidgetSummary {
  verdict: EVerdict
  act: number
  watch: number
  error: boolean
}

/** One entry of companyHome.getDirectory: a company and the project keys that roll up to it. */
export interface ICompanyEntry {
  slug: string
  name: string
  color: string | null
  projects: string[]
}
